import React, { useState } from 'react';
import {
    Sparkles, Mail, Lock, Eye, EyeOff, ArrowRight,
    User, Chrome, Github, Loader2, KeyRound,
    CheckCircle2, AlertCircle
} from 'lucide-react';

interface BordioLoginProps {
    onLogin: (email: string, password: string) => Promise<boolean>;
    onRegister?: (displayName: string, email: string, password: string) => Promise<boolean>;
    onSocialLogin: (provider: 'google' | 'github' | 'microsoft') => Promise<boolean>;
    onSendOtp?: (email: string) => Promise<{ success: boolean; message: string; otp_test_fallback?: string }>;
    onOtpLogin?: (email: string, otp: string) => Promise<boolean>;
    isLoading: boolean;
    error: string | null;
    onBackToHome?: () => void;
}

type AuthMode = 'login' | 'register' | 'otp';

export const BordioLogin: React.FC<BordioLoginProps> = ({
    onLogin,
    onRegister,
    onSocialLogin,
    onSendOtp,
    onOtpLogin,
    isLoading,
    error,
}) => {
    const [mode, setMode] = useState<AuthMode>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpMessage, setOtpMessage] = useState('');
    const [otpFallback, setOtpFallback] = useState('');

    const isAdliftEmail = email.trim().toLowerCase().endsWith('@adlift.com');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedEmail = email.trim().toLowerCase();
        if (trimmedEmail.endsWith('@adlift.com') && mode !== 'otp') {
            setMode('otp');
            setOtpSent(false);
            setOtp('');
            if (onSendOtp) {
                const result = await onSendOtp(email);
                if (result.success) {
                    setOtpSent(true);
                    setOtpMessage("Adlift users must use OTP. " + result.message);
                    if (result.otp_test_fallback) {
                        setOtpFallback(result.otp_test_fallback);
                    }
                }
            }
            return;
        }

        if (mode === 'login') {
            await onLogin(email, password);
        } else if (mode === 'register' && onRegister) {
            await onRegister(displayName, email, password);
        } else if (mode === 'otp') {
            if (!otpSent && onSendOtp) {
                const result = await onSendOtp(email);
                if (result.success) {
                    setOtpSent(true);
                    setOtpMessage(result.message);
                    if (result.otp_test_fallback) {
                        setOtpFallback(result.otp_test_fallback);
                    }
                }
            } else if (otpSent && onOtpLogin) {
                await onOtpLogin(email, otp);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-surface-50 via-white to-brand-50 flex">
            {/* Left Panel - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
                    <div className="absolute bottom-20 right-20 w-96 h-96 bg-brand-300 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white rounded-full blur-2xl" />
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    <div>
                        <div className="flex items-center gap-3 mb-16">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                <img src="/planner-logo.png" alt="Planner" className="w-7 h-7 object-contain" />
                            </div>
                            <span className="text-xl font-bold text-white">Planner</span>
                        </div>

                        <h1 className="text-4xl font-bold text-white leading-tight mb-6">
                            Plan, organize, and<br />
                            track your work<br />
                            <span className="text-brand-200">effortlessly.</span>
                        </h1>
                        <p className="text-lg text-brand-200 max-w-md leading-relaxed">
                            A beautiful project management tool that helps teams collaborate,
                            plan sprints, and deliver projects on time.
                        </p>
                    </div>

                    {/* Feature highlights */}
                    <div className="space-y-4">
                        {[
                            'Weekly planner with drag & drop',
                            'Kanban boards for visual workflow',
                            'Team collaboration & real-time updates',
                            'Sprint planning & analytics',
                        ].map((feature, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <CheckCircle2 className="w-5 h-5 text-brand-200 flex-shrink-0" />
                                <span className="text-sm text-white/90">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Right Panel - Auth Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-700 rounded-xl flex items-center justify-center">
                            <img src="/planner-logo.png" alt="Planner" className="w-7 h-7 object-contain" />
                        </div>
                        <span className="text-xl font-bold text-surface-900">Planner</span>
                    </div>

                    {/* Header */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-surface-900">
                            {mode === 'login' ? 'Welcome back' : mode === 'register' ? 'Create account' : 'Sign in with OTP'}
                        </h2>
                        <p className="text-surface-500 mt-2">
                            {mode === 'login'
                                ? 'Sign in to your account to continue'
                                : mode === 'register'
                                    ? 'Get started with your free account'
                                    : 'We\'ll send a one-time code to your email'}
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3 animate-slide-up">
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-red-800">Authentication failed</p>
                                <p className="text-sm text-red-600 mt-0.5">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Social Login */}
                    {mode !== 'otp' && (
                        <>
                            <div className="grid grid-cols-2 gap-3 mb-6">
                                <button
                                    onClick={() => onSocialLogin('google')}
                                    disabled={isLoading}
                                    className="btn-secondary py-2.5 text-sm"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Google
                                </button>
                                <button
                                    onClick={() => onSocialLogin('github')}
                                    disabled={isLoading}
                                    className="btn-secondary py-2.5 text-sm"
                                >
                                    <Github className="w-4 h-4" />
                                    GitHub
                                </button>
                            </div>

                            <div className="relative mb-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-surface-200" />
                                </div>
                                <div className="relative flex justify-center text-xs">
                                    <span className="px-3 bg-white text-surface-400">or continue with email</span>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {mode === 'register' && (
                            <div>
                                <label className="block text-sm font-medium text-surface-700 mb-1.5">Full Name</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                                    <input
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="John Doe"
                                        className="input-field pl-10 py-2.5"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-surface-700 mb-1.5">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="input-field pl-10 py-2.5"
                                    required
                                    disabled={mode === 'otp' && otpSent}
                                />
                            </div>
                            {email.trim().toLowerCase().endsWith('@adlift.com') && mode !== 'otp' && (
                                <p className="text-xs text-brand-600 font-medium mt-2 animate-fade-in">
                                    🔒 Adlift accounts use OTP login. Submitting will send a verification code.
                                </p>
                            )}
                        </div>

                        {mode === 'otp' && otpSent ? (
                            <div>
                                <label className="block text-sm font-medium text-surface-700 mb-1.5">Verification Code</label>
                                <div className="relative">
                                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                                    <input
                                        type="text"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        placeholder="Enter 6-digit code"
                                        className="input-field pl-10 py-2.5 text-center text-lg tracking-widest"
                                        maxLength={6}
                                        required
                                    />
                                </div>
                                {otpMessage && (
                                    <p className="text-xs text-green-600 mt-1.5">{otpMessage}</p>
                                )}
                            </div>
                        ) : (mode !== 'otp' && !isAdliftEmail) ? (
                            <div>
                                <label className="block text-sm font-medium text-surface-700 mb-1.5">Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="input-field pl-10 pr-10 py-2.5"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        {mode === 'login' && !isAdliftEmail && (
                            <div className="flex items-center justify-between">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" className="w-4 h-4 rounded border-surface-300 text-brand-600 focus:ring-brand-500" />
                                    <span className="text-sm text-surface-600">Remember me</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => { setMode('otp'); setOtpSent(false); setOtp(''); }}
                                    className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                                >
                                    Sign in with OTP
                                </button>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="btn-primary w-full py-3 text-sm font-semibold shadow-md hover:shadow-lg transition-shadow"
                        >
                            {isLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    {isAdliftEmail ? (otpSent ? 'Verify & Sign in' : 'Send Code') : (mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : otpSent ? 'Verify & Sign in' : 'Send Code')}
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Mode Switch */}
                    <div className="mt-6 text-center">
                        {mode === 'login' ? (
                            <p className="text-sm text-surface-500">
                                Don't have an account?{' '}
                                <button
                                    onClick={() => setMode('register')}
                                    className="text-brand-600 hover:text-brand-700 font-semibold"
                                >
                                    Sign up
                                </button>
                            </p>
                        ) : (
                            <p className="text-sm text-surface-500">
                                Already have an account?{' '}
                                <button
                                    onClick={() => { setMode('login'); setOtpSent(false); setOtp(''); }}
                                    className="text-brand-600 hover:text-brand-700 font-semibold"
                                >
                                    Sign in
                                </button>
                            </p>
                        )}
                    </div>

                    {/* Footer */}
                    <p className="mt-8 text-center text-xs text-surface-400">
                        By continuing, you agree to our Terms of Service and Privacy Policy.
                    </p>
                </div>
            </div>
        </div>
    );
};
